import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import ModalCloseButton from './ModalCloseButton';
import './FilterHelpModal.css';

/**
 * FilterHelpModal Component
 *
 * Educational modal that explains AND/OR filter logic.
 * Styled to match AlertHelpModal: clean sections, tables, and key info lists.
 */
function FilterHelpModal({ isOpen, onClose, filterType = 'devices' }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const contentByType = {
    devices: {
      title: 'Device Filters & Help',
      logicRows: [
        {
          scope: 'Within a section',
          logic: 'OR',
          meaning: 'Devices matching any selected option in that section are shown',
        },
        {
          scope: 'Between sections',
          logic: 'AND',
          meaning: 'Devices must match all active sections',
        },
        {
          scope: 'Quick filter buttons',
          logic: 'OR',
          meaning: 'Toggle on/off; multiple buttons in the same category combine with OR',
        },
      ],
      exampleRows: [
        {
          scenario: 'Coverage Status',
          selection: '"Coverage Expiring" and "Coverage Expired"',
          result: 'Devices that are either expiring soon or already expired',
        },
        {
          scenario: 'Department',
          selection: '"Cardiology" and "Radiology"',
          result: 'Devices from either department',
        },
        {
          scenario: 'Coverage + Department',
          selection: 'Coverage: Expiring/Expired · Dept: Cardiology',
          result: 'Cardiology devices that are also expiring or expired',
        },
        {
          scenario: 'Maintenance + Date Range',
          selection: 'Overdue PPM · Installed Jan–Mar 2025',
          result: 'Devices with overdue PPM installed in that date range',
        },
      ],
      keyInfo: [
        {
          label: 'Same section',
          text: 'Use OR logic — select multiple options to see any match',
        },
        {
          label: 'Different sections',
          text: 'Use AND logic — all active sections must match',
        },
        {
          label: 'Want more results',
          text: 'Add more options within the same section (OR)',
        },
        {
          label: 'Want fewer results',
          text: 'Add another section filter (AND)',
        },
      ],
    },
    complaints: {
      title: 'Complaint Filters & Help',
      logicRows: [
        {
          scope: 'Within a filter type',
          logic: 'OR',
          meaning: 'Complaints matching any selected option in that type are shown (e.g. Status: Open or In Progress)',
        },
        {
          scope: 'Different filter types',
          logic: 'AND',
          meaning: 'Selections across types must all match (e.g. Status and Severity)',
        },
        {
          scope: 'Between sections',
          logic: 'AND',
          meaning: 'All active sections must match (Status & Severity, Department, Date Ranges)',
        },
        {
          scope: 'Date range filters',
          logic: 'AND',
          meaning: 'Reported Date, Resolution Date, and Days Open combine with AND when used together',
        },
      ],
      exampleRows: [
        {
          scenario: 'Multiple statuses',
          selection: 'Status: "Open" and "In Progress"',
          result: 'Complaints that are either open or in progress',
        },
        {
          scenario: 'Status + Severity',
          selection: 'Status: Open/In Progress · Severity: Critical',
          result: 'Open or in-progress complaints that are also critical',
        },
        {
          scenario: 'Multiple sections',
          selection: 'Open · Emergency · Created last 7 days',
          result: 'Open complaints in Emergency created in the last 7 days',
        },
        {
          scenario: 'Filed by me',
          selection: 'Quick filter: Filed by me (+ optional Status/Severity)',
          result: 'Complaints you reported; combines with other filters using AND',
        },
        {
          scenario: 'Date ranges combined',
          selection: 'Reported Jan · Resolved Jan · Days Open 5–20',
          result: 'Complaints matching all three date conditions',
        },
      ],
      keyInfo: [
        {
          label: 'Same filter type',
          text: 'Use OR logic (Status: Open or In Progress)',
        },
        {
          label: 'Different filter types',
          text: 'Use AND logic (Status and Severity both apply)',
        },
        {
          label: 'Different sections',
          text: 'Use AND logic — all sections must match',
        },
        {
          label: 'Resolution Date + Open status',
          text: 'Won\'t find results — use Reported Date or Days Open instead',
        },
        {
          label: 'Want more results',
          text: 'Add more options within a filter type (OR)',
        },
        {
          label: 'Want fewer results',
          text: 'Add another filter type or section (AND)',
        },
      ],
    },
    maintenance: {
      title: 'Maintenance History Filters & Help',
      logicRows: [
        {
          scope: 'Auto-selection',
          logic: 'Auto',
          meaning: 'Selecting a specific filter (e.g. PPM) automatically activates its type (e.g. Maintenance Only)',
        },
        {
          scope: 'Maintenance Type vs Complaint Status',
          logic: 'XOR',
          meaning: 'Cannot select both together — incompatible options are disabled while the other is active',
        },
        {
          scope: 'Within Maintenance Type or Complaint Status',
          logic: 'OR',
          meaning: 'Multiple options of the same kind combine with OR (e.g. PPM or Calibration)',
        },
        {
          scope: 'Between different filter sections',
          logic: 'AND',
          meaning: 'Updated date range, department, type, and search must all match when used together',
        },
      ],
      exampleRows: [
        {
          scenario: 'PPM quick filter',
          selection: 'PPM',
          result: 'PPM maintenance records only — auto-activates Maintenance Only',
        },
        {
          scenario: 'Closed complaints',
          selection: 'Closed',
          result: 'Closed complaint records only — auto-activates Complaints Only',
        },
        {
          scenario: 'Clear specific filter',
          selection: 'Deselect PPM while Complaints Only stays on',
          result: 'Only clears PPM; type filters stay until you clear them manually',
        },
        {
          scenario: 'Updated date + department',
          selection: 'Last 30d · Department: Cardiology',
          result: 'Cardiology records updated in the last 30 days',
        },
      ],
      keyInfo: [
        {
          label: 'Auto-selection',
          text: 'Type-specific filters (PPM, Closed, etc.) turn on the matching type filter automatically',
        },
        {
          label: 'Updated date',
          text: 'Date filters and the Updated column use when the record was last updated (recent activity). Logged/created date stays in expanded details.',
        },
        {
          label: 'Mutual exclusivity',
          text: 'Maintenance Type and Complaint Status cannot be active at the same time',
        },
        {
          label: 'Manual clearing',
          text: 'Deselecting a specific filter only clears that option — clear the type button separately if needed',
        },
        {
          label: 'Search',
          text: 'Search by complaint ID, device ID, name, department, technician, or description',
        },
      ],
    },
  };

  const config = contentByType[filterType] || contentByType.devices;

  const content = (
    <div className="filter-help-overlay" onClick={onClose}>
      <div className="filter-help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="filter-help-header">
          <h2>{config.title}</h2>
          <ModalCloseButton onClick={onClose} title="Close" />
        </div>

        <div className="filter-help-body">
          <section className="filter-help-section">
            <h3>How Filters Combine</h3>
            <table className="filter-help-table">
              <thead>
                <tr>
                  <th>Scope</th>
                  <th>Logic</th>
                  <th>What it means</th>
                </tr>
              </thead>
              <tbody>
                {config.logicRows.map((row, idx) => (
                  <tr key={idx}>
                    <td><strong>{row.scope}</strong></td>
                    <td>{row.logic}</td>
                    <td>{row.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="filter-help-section">
            <h3>Examples</h3>
            <table className="filter-help-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Selection</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {config.exampleRows.map((row, idx) => (
                  <tr key={idx}>
                    <td><strong>{row.scenario}</strong></td>
                    <td>{row.selection}</td>
                    <td>{row.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="filter-help-section">
            <h3>Key Information</h3>
            <ul>
              {config.keyInfo.map((item, idx) => (
                <li key={idx}>
                  <strong>{item.label}:</strong> {item.text}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

export default FilterHelpModal;
